import React from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { getMarketProducts, getSelectedProduct } from 'ducks/products';
import { useIntl } from 'react-intl';
import Height from './Height';
import Depth from './Depth';
import Width from './Width';
import { useCore } from 'contexts/core';
import {
  replaceFrameWithSlidingDoors,
  changeFrameSize,
  updateOuterLightsOnFrame,
  updateInnerLightsOnFrame
} from 'rangeConfig/pax/utils/changeSize/frameUtils';
import replaceDoors from 'rangeConfig/pax/utils/changeSize/replaceDoors';
import { Dimension, ProductType } from '@inter-ikea-storageone/range';
import { getFilterOptions } from 'filters/SizeFilter/utils';
import { getMatchingFrames, getMatchingWidthsOfFrames } from '../../utils/changeSize/productUtils';
import { getQuery } from 'utils/queryUtils';
import { useBehaviorTracker } from 'contexts/behaviors';

const query = getQuery();
const enableChangeWidth = query.get('enableChangeWidth') === 'true';

const SizeContainer = styled.div`
  padding: ${({ theme }) => theme.space.s} 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.space.xxl};
`;

const ChangeSize = () => {
  const { formatMessage } = useIntl();
  const products = useSelector(getMarketProducts);
  const selectedProduct = useSelector(getSelectedProduct);
  const coreApi = useCore();
  const behaviorTracker = useBehaviorTracker();

  // We filter all frames that have an equal width, depth and color to the selected frame
  const framesWithSameHeight = getMatchingFrames(selectedProduct, products, 'height');
  const framesWithSameDepth = getMatchingFrames(selectedProduct, products, 'depth');
  const framesWithSameHeightAndDepth = getMatchingWidthsOfFrames(selectedProduct, products);
  const availableDepths = getFilterOptions('depth')(framesWithSameHeight);
  const availableHeights = getFilterOptions('height')(framesWithSameDepth);
  const availableWidths = getFilterOptions('width')(framesWithSameHeightAndDepth);

  const selectedEntity = coreApi.entities.findSelectedEntity();

  // Changes the height of the selected frame
  const handleSizeChange = (
    size: number,
    setFilterState: any,
    framesMatchingSelectedFrame: any,
    replaceableDoors: any,
    matchFrameBy: Dimension,
    dimensionType: Dimension
  ) => {
    const meta = { size: { dimension: dimensionType, value: size } };

    behaviorTracker.withMeta(meta).custom(() => {
      setFilterState({ selected: size });

      if (!selectedEntity) return;

      let newFrameToSelect: any;

      const slidingDoorOnFrame = coreApi.entities.findEntityInHierarchyWithOneOfProductTypes(
        selectedEntity,
        [ProductType.PAX_SLIDING_DOOR_REGULAR_ASM, ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM]
      );
      const outerLightsOnFrame = coreApi.entities.getChildrenWithProductType(
        selectedEntity,
        ProductType.PAX_OUTER_LIGHT
      );
      const innerLightsOnFrame = [
        ...coreApi.entities.getChildrenWithProductType(selectedEntity, ProductType.PAX_INNER_LIGHT),
        ...coreApi.entities.getChildrenWithProductType(
          selectedEntity,
          ProductType.PAX_INNER_LIGHT_OVERSIDAN
        )
      ];

      if (slidingDoorOnFrame) {
        newFrameToSelect = replaceFrameWithSlidingDoors(
          slidingDoorOnFrame,
          products,
          size,
          selectedEntity,
          coreApi,
          framesMatchingSelectedFrame,
          replaceableDoors,
          matchFrameBy,
          dimensionType
        );
      } else {
        // First step: Change the height of the selected frame
        newFrameToSelect = changeFrameSize(
          coreApi,
          framesMatchingSelectedFrame,
          size,
          selectedEntity,
          dimensionType,
          products
        );
      }

      if (newFrameToSelect) {
        updateOuterLightsOnFrame(newFrameToSelect, outerLightsOnFrame, coreApi);
        updateInnerLightsOnFrame(newFrameToSelect, innerLightsOnFrame, coreApi);
        if (replaceableDoors && dimensionType === 'height')
          replaceDoors(coreApi, newFrameToSelect, replaceableDoors);
        coreApi.entities.select(newFrameToSelect);
      }
    });
  };

  return (
    <SizeContainer>
      <Grid>
        <Height
          fluidWidth={true}
          title={formatMessage({ id: 'storageone.height' })}
          products={products}
          selectedProduct={selectedProduct}
          selectedEntity={selectedEntity}
          onSizeChange={handleSizeChange}
          framesWithSameDepth={framesWithSameDepth}
          availableHeights={availableHeights}
        />
        <Depth
          fluidWidth={true}
          title={formatMessage({ id: 'storageone.depth' })}
          products={products}
          selectedProduct={selectedProduct}
          selectedEntity={selectedEntity}
          onSizeChange={handleSizeChange}
          framesWithSameHeight={framesWithSameHeight}
          availableDepths={availableDepths}
        />
      </Grid>
      {enableChangeWidth && (
        <Width
          products={products}
          selectedProduct={selectedProduct}
          selectedEntity={selectedEntity}
          onSizeChange={handleSizeChange}
          framesWithSameWidth={framesWithSameHeightAndDepth}
          availableWidths={availableWidths}
        />
      )}
    </SizeContainer>
  );
};

export default ChangeSize;
